<script setup lang="ts">
import type { FieldSize } from '@/components/fields/KTextField.vue';

import { computed, ref, watch } from 'vue';

import useNumberField from '@/composables/useNumberField';

export interface Props {
  min?: number;
  max?: number;
  step?: number;
  largeStep?: number;
  size?: FieldSize;
  disabled?: boolean;
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  min: 1,
  max: 9999,
  step: 1,
  largeStep: 10,
  size: 'md',
  disabled: false,
  readonly: false,
});

const model = defineModel<number>({ default: 1 });

const current = computed(() => model.value);

const { isError, blinkError, handleKeypress } = useNumberField();

const inputValue = ref(String(current.value));

watch(current, (value) => {
  inputValue.value = String(value);
});

const decDisabled = computed(
  () => props.disabled || props.readonly || current.value <= props.min,
);
const incDisabled = computed(
  () => props.disabled || props.readonly || current.value >= props.max,
);

const sizeClass = computed(() => 'size-' + props.size);

function setValue(value: number): void {
  if (props.disabled || props.readonly) return;
  const clamped = Math.min(props.max, Math.max(props.min, value));
  inputValue.value = String(clamped);
  if (clamped === current.value) return;
  model.value = clamped;
}

function decrement(): void {
  setValue(current.value - props.step);
}

function increment(): void {
  setValue(current.value + props.step);
}

function handleInput(event: Event): void {
  const digitsOnly = (event.target as HTMLInputElement).value.replace(
    /\D/g,
    '',
  );
  if (digitsOnly === '') {
    inputValue.value = '';
    return;
  }
  if (Number(digitsOnly) > props.max) {
    blinkError();
    inputValue.value = String(props.max);
    return;
  }
  inputValue.value = digitsOnly;
}

function commitInput(): void {
  const parsed = parseInt(inputValue.value, 10);
  if (Number.isNaN(parsed)) {
    blinkError();
    inputValue.value = String(current.value);
    return;
  }
  setValue(parsed);
}

function handleKeydown(event: KeyboardEvent): void {
  switch (event.key) {
    case 'ArrowUp':
      event.preventDefault();
      increment();
      break;
    case 'ArrowDown':
      event.preventDefault();
      decrement();
      break;
    case 'PageUp':
      event.preventDefault();
      setValue(current.value + props.largeStep);
      break;
    case 'PageDown':
      event.preventDefault();
      setValue(current.value - props.largeStep);
      break;
    case 'Home':
      event.preventDefault();
      setValue(props.min);
      break;
    case 'End':
      event.preventDefault();
      setValue(props.max);
      break;
    case 'Enter':
      commitInput();
      (event.target as HTMLInputElement).blur();
      break;
  }
}
</script>

<template>
  <div
    :class="[$style.kNumberStepperField, $style[sizeClass]]"
    data-testid="kNumberStepperField"
  >
    <button
      type="button"
      :class="$style.btn"
      :disabled="decDisabled"
      tabindex="-1"
      aria-label="Decrease"
      @click="decrement"
    >
      <v-icon icon="mdi-minus" size="14" />
    </button>
    <input
      :class="[$style.value, { [$style.valueError]: isError }]"
      :value="inputValue"
      :disabled="props.disabled"
      :readonly="props.readonly"
      inputmode="numeric"
      role="spinbutton"
      :aria-valuemin="props.min"
      :aria-valuemax="props.max"
      :aria-valuenow="current"
      @input="handleInput"
      @keypress="handleKeypress"
      @keydown="handleKeydown"
      @blur="commitInput"
    />
    <button
      type="button"
      :class="$style.btn"
      :disabled="incDisabled"
      tabindex="-1"
      aria-label="Increase"
      @click="increment"
    >
      <v-icon icon="mdi-plus" size="14" />
    </button>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/brand-primary' as bp;

.kNumberStepperField {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid colors.$color-gray-300;
  background-color: colors.$color-white;
  height: 40px;

  &:focus-within {
    outline: 2px solid bp.$color-brand-400;
    outline-offset: 2px;
  }
}
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    background-color: transparent;
    color: colors.$color-text-secondary;
    cursor: pointer;
    border: none;
    padding: 0;
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      background-color: bp.$color-brand-50;
      color: bp.$color-brand-700;
    }
    &:active:not(:disabled) {
      background-color: bp.$color-brand-100;
    }
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
  .value {
    width: 36px;
    text-align: center;
    font-size: 0.875rem;
    font-weight: 500;
    color: colors.$color-text-primary;
    align-self: center;
    padding: 0 4px;
    border: none;
    background-color: transparent;
    outline: none;

    &:disabled {
      cursor: not-allowed;
    }
  }
  .valueError {
    color: colors.$color-red-700;
  }

.size-sm {
  height: 32px;
}
.size-sm .btn {
  width: 28px;
}
.size-lg {
  height: 48px;
}
.size-lg .btn {
  width: 36px;
}
.size-lg .value {
  font-size: 1rem;
  width: 44px;
}
</style>
